import React from 'react';
import { Task } from '@/types/tasks';
import { BatchOptions, DEFAULT_MAX_RATE_LIMIT_WAIT_MS, TaskDefinition, UITask } from '@/context/house/tasks/types';
import { actions as uiActions } from '@/context/house/tasks/state/uiTaskReducer';
import { logger, logTask } from '@/context/house/tasks/logging';
import { getHouseIdFromMeta, isStatusActive, isTaskDelete, makeDeleteKeyFromMeta } from '@/context/house/tasks/helpers';
import { generateBatchId } from '@/context/house/tasks/managerUtils';
import type { Batch } from '@/context/house/tasks/batchManager';

/**
 * Produces the scheduleTaskBatch callback used by HouseTaskManagerContext.
 *
 * Why: extracting this keeps the provider component declarative and focused on wiring,
 * while the imperative scheduling and deduplication logic lives in a cohesive module.
 */
export function createScheduleTaskBatch(deps: {
	processQueuedTasks: () => void;
	uiTasksRef: React.MutableRefObject<UITask[]>;
	globalTasksRef: React.MutableRefObject<Map<string, UITask>>;
	batchesRef: React.MutableRefObject<Map<string, Batch>>;
	runtime: { activeDeleteKeys: Set<string>; releaseDispatched: (id: string) => void };
	dispatch: React.Dispatch<
		| ReturnType<typeof uiActions.addMany>
		| ReturnType<typeof uiActions.replaceAll>
		| ReturnType<typeof uiActions.updateStatus>
	>;
	houseId: number;
}) {
	const { processQueuedTasks, uiTasksRef, globalTasksRef, batchesRef, runtime, dispatch, houseId } = deps;

	return async (taskDefinitions: TaskDefinition[], options: BatchOptions = {}): Promise<void> => {
		if (taskDefinitions.length === 0) return;

		const batchId = generateBatchId();
		const { maxRateLimitWaitMs = DEFAULT_MAX_RATE_LIMIT_WAIT_MS, showNotification } = options;

		const existingTaskIds = new Set(uiTasksRef.current.map((task) => task.id));
		const seenDeleteKeys = new Set<string>();

		const newTaskDefinitions = taskDefinitions.filter((def) => {
			if (existingTaskIds.has(def.id)) {
				logger.info(`Skipping duplicate task ${def.id} - already scheduled`);
				return false;
			}
			// Delete dedupe
			if (isTaskDelete({ taskType: def.taskType })) {
				const key = makeDeleteKeyFromMeta(def.metadata as Record<string, unknown>);
				if (key) {
					if (seenDeleteKeys.has(key)) {
						logger.info(`Skipping duplicate delete in same scheduling call for ${key}`);
						return false;
					}
					if (runtime.activeDeleteKeys.has(key)) {
						logger.info(`Skipping duplicate delete for ${key} — active key exists`);
						return false;
					}
					const hasActiveSameDoc = uiTasksRef.current.some((t) => {
						if (!isTaskDelete(t)) return false;
						const tKey = makeDeleteKeyFromMeta(t.execution.apiTask.metadata as Record<string, unknown>);
						return tKey === key && isStatusActive(t.execution.apiTask.status);
					});
					if (hasActiveSameDoc) {
						logger.info(`Skipping duplicate delete for ${key} — active task exists`);
						return false;
					}
					seenDeleteKeys.add(key);
					runtime.activeDeleteKeys.add(key);
				}
			}
			return true;
		});

		if (newTaskDefinitions.length === 0) {
			logger.info(`No new tasks to schedule in batch ${batchId} - all were duplicates`);
			return;
		}

		const skipped = taskDefinitions.length - newTaskDefinitions.length;
		logger.info(
			`Batch ${batchId} intake: received ${taskDefinitions.length}, accepted ${newTaskDefinitions.length}, skipped ${skipped}`,
		);
		logger.info(`Scheduling batch ${batchId} with ${newTaskDefinitions.length} tasks`);

		const newUITasks: UITask[] = newTaskDefinitions.map((def) => {
			const originHouseId = getHouseIdFromMeta(def.metadata as Record<string, unknown>) ?? houseId;
			const apiTask: Task = {
				id: def.id,
				status: 'queued',
				type: def.taskType,
				createdAt: new Date().toISOString(),
				metadata: {
					...(def.metadata as Record<string, unknown>),
					houseId: originHouseId,
					batchId,
				},
			};

			const executionTask = {
				executeFn: def.executeFn,
				onComplete: def.onComplete,
				onAccepted: def.onAccepted,
				apiTask,
				retryCount: 0,
				maxRateLimitWaitMs,
				showNotification,
			};

			const uiTask: UITask = {
				id: def.id,
				batchId,
				taskType: def.taskType,
				metadata: def.metadata as Record<string, unknown>,
				execution: executionTask,
			};

			globalTasksRef.current.set(def.id, uiTask);
			logTask(def.id, 'queued', 'info', `Queued in batch ${batchId}`);
			return uiTask;
		});

		batchesRef.current.set(batchId, {
			options,
			taskIds: newTaskDefinitions.map((def) => def.id),
			completedCount: 0,
			failedCount: 0,
			skippedCount: 0,
			cancelledCount: 0,
			pausedCount: 0,
			duplicateFiles: new Map(),
		});

		dispatch(uiActions.addMany(newUITasks));

		setTimeout(() => {
			processQueuedTasks();
		}, 0);
	};
}
